import {Component, OnDestroy, OnInit} from '@angular/core';
import {gql} from '@apollo/client';
import {Apollo} from 'apollo-angular';
import {Subscription} from 'rxjs';
import {
  QuestionnaireListComponent_getQuestionnaires,
  QuestionnaireListComponent_getQuestionnaires_outstandingQuestionnaires_edges_node,
} from './__generated__/QuestionnaireListComponent_getQuestionnaires';
import {catchError, debounceTime, map, startWith, switchMap} from 'rxjs/operators';
import {FormControl} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {QuestionnaireListComponent_getRole} from './__generated__/QuestionnaireListComponent_getRole';
import {QuestionnaireStateEnum, UserRole} from '../shared/graphql/globalTypes';
import {ErrorService} from '../shared/services/error.service';

const GET_ROLE = gql`
  query QuestionnaireListComponent_getRole {
    me {
      role
    }
  }
`;

const GET_QUESTIONNAIRES = gql`
  query QuestionnaireListComponent_getQuestionnaires($search: String) {
    outstandingQuestionnaires(search: $search, orderBy: ["-created"]) {
      edges {
        node {
          id
          clientFirstName
          clientLastName
          created
          createdBy {
            firstName
            lastName
            tenant {
              name
            }
          }
          phoneNumber
          session {
            id
          }
          resultsUrl
          state
        }
      }
    }
  }
`;

@Component({
  selector: 'app-questionnaire-list',
  templateUrl: './questionnaire-list.component.html',
  styleUrls: ['./questionnaire-list.component.scss'],
})
export class QuestionnaireListComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  searchControl = new FormControl('');
  questionnaires: QuestionnaireListComponent_getQuestionnaires_outstandingQuestionnaires_edges_node[] =
    [];

  userRole: UserRole | null = null;

  constructor(
    private apollo: Apollo,
    private translate: TranslateService,
    private errorHandler: ErrorService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.apollo
        .query<QuestionnaireListComponent_getRole>({
          query: GET_ROLE,
        })
        .pipe(
          map(output => output.data.me.role),
          catchError(error => this.errorHandler.handleApollo(error)),
        )
        .subscribe(role => (this.userRole = role)),

      this.searchControl.valueChanges
        .pipe(
          startWith(''),
          debounceTime(300),
          switchMap(s => {
            return this.apollo.watchQuery<QuestionnaireListComponent_getQuestionnaires>({
              query: GET_QUESTIONNAIRES,
              variables: {
                search: s,
              },
            }).valueChanges;
          }),
          catchError(error => this.errorHandler.handleApollo(error)),
        )
        .subscribe(output => {
          this.questionnaires = output.data.outstandingQuestionnaires.edges.map(e => e.node);
        }),
    );
  }

  getStateText(state: QuestionnaireStateEnum | null) {
    switch (state) {
      case 'MADE':
        return this.translate.instant('MADE');
      case 'SENT':
        return this.translate.instant('SENT');
    }

    return 'N/A';
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  toResult(
    outstandingQuestionnaire: QuestionnaireListComponent_getQuestionnaires_outstandingQuestionnaires_edges_node,
  ) {
    if (outstandingQuestionnaire.session === null) {
      return;
    }

    if (outstandingQuestionnaire.resultsUrl !== null) {
      window.open(outstandingQuestionnaire.resultsUrl, '_blank');
    }
  }
}
